// Currency formatting for money that belongs to the CUSTOMER — document totals,
// dashboard summaries, sales reports.
//
// Deliberately NOT for the package prices under `components/company/` and
// `views/company/setting-packages.vue`: those are what Sala Daeng charges for
// the service, always in baht, and must stay in baht for a Lao customer too.
//
// The currency follows the company's `countryCode`, the field the add-company
// form and the edit-company dialog already write (see `companyCountries.js`).
// A company with no countryCode — every company predating that field — falls
// back to Thailand, so nothing changes for them.
//
// To support another currency: add it to CURRENCIES here and put its code on
// the country entry in `companyCountries.js`. Call sites need no change.

import { getCountryConfig } from './companyCountries'

export const DEFAULT_CURRENCY_CODE = 'THB'

const CURRENCIES = {
  THB: {
    symbol: '฿',
    locale: 'th-TH',
    decimals: 2,
  },
  LAK: {
    symbol: '₭',
    // NOT 'lo-LA': ICU groups that locale with a dot, so 240000 renders as
    // "240.000", which reads as a decimal. Lao tax documents group with a
    // comma — the Lao PDF templates in functions/ already print "240,000.00" —
    // so this matches what the customer sees on their own invoice.
    locale: 'en-US',
    // Kip has no subunit in practice: an invoice reads 240,000, not
    // 240,000.00. Per-currency, not a shared constant.
    decimals: 0,
  },
}

// Firestore stores money as an integer scaled by 100 (see the `/ 100` that used
// to sit at every call site). Kept in one place so the scale is not re-derived.
const AMOUNT_SCALE = 100

/** Always returns a config; unknown / missing country → the default currency. */
export function getCurrencyConfig(countryCode) {
  const country = getCountryConfig(countryCode)
  const code = (country && country.currency) || DEFAULT_CURRENCY_CODE
  return CURRENCIES[code] || CURRENCIES[DEFAULT_CURRENCY_CODE]
}

/** The bare symbol, for templates that place it themselves. */
export function getCurrencySymbol(countryCode) {
  return getCurrencyConfig(countryCode).symbol
}

/**
 * Format a scaled amount for display, without a symbol.
 * @param {number} scaledAmount amount × 100, as stored
 * @param {string} countryCode the company's countryCode
 */
export function formatAmount(scaledAmount, countryCode) {
  const config = getCurrencyConfig(countryCode)
  // Number(null) is 0, so a missing amount would otherwise print as "0" and be
  // read as a real zero total. Absent and zero are different things here.
  if (scaledAmount === null || scaledAmount === undefined || scaledAmount === '') return ''
  const value = Number(scaledAmount)
  if (isNaN(value)) return ''
  return (value / AMOUNT_SCALE).toLocaleString(config.locale, {
    minimumFractionDigits: config.decimals,
    maximumFractionDigits: config.decimals,
  })
}

/**
 * Format a scaled amount with its symbol, in the position that currency uses.
 * Both currencies here suffix it, which is also how the dashboard read before.
 */
export function formatMoney(scaledAmount, countryCode) {
  const formatted = formatAmount(scaledAmount, countryCode)
  if (formatted === '') return ''
  return formatted + ' ' + getCurrencySymbol(countryCode)
}
